import type { UIMessage } from "ai";

/** How the API says a turn ended — see `ChatOutcome` in api/src/context/chat. */
export type SparkChatOutcome =
  | "answered"
  | "crisis"
  | "identity"
  | "blocked_local"
  | "blocked_input"
  | "blocked_output"
  | "moderation_failed";

/**
 * How a turn came about and what it cost — see `debugInfoOf` in
 * api/src/context/chat. Only sent while the API's test panel is on.
 */
export type SparkChatDebugInfo = {
  /** The layer that decided what the student reads. */
  decidedBy: "local_check" | "input_moderation" | "model" | "output_moderation";
  /** False where the text is a fixed reply rather than what the model wrote. */
  generated: boolean;
  categories: string[];
  piiRedacted: boolean;
  /** Missing where the turn ended before the model was asked. */
  inputTokens?: number;
  outputTokens?: number;
  model: string;
  durationMs: number;
};

export type SparkChatMessage = UIMessage<
  never,
  { outcome: { outcome: SparkChatOutcome }; debug: SparkChatDebugInfo }
>;

export type SparkChatSource = { id: string; title: string };

export const textOf = (message: SparkChatMessage) =>
  message.parts
    .filter((p) => p.type === "text")
    .map((p) => p.text)
    .join("");

export const sourcesOf = (message: SparkChatMessage): SparkChatSource[] =>
  message.parts
    .filter((p) => p.type === "source-document")
    .map((p) => ({ id: p.sourceId, title: p.title }));

export const outcomeOf = (
  message: SparkChatMessage,
): SparkChatOutcome | undefined =>
  message.parts.find((p) => p.type === "data-outcome")?.data.outcome;

export const debugOf = (
  message: SparkChatMessage,
): SparkChatDebugInfo | undefined =>
  message.parts.find((p) => p.type === "data-debug")?.data;
